import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from '../home/components/Footer';

type CalculatorType = 'fib4' | 'fib3' | 'mfs';

const FIB4CalculatorPage = () => {
  const navigate = useNavigate();
  
  // ✅ react-intersection-observer を使わない実装
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  const [activeCalculator, setActiveCalculator] = useState<CalculatorType>('fib4');
  const [gender, setGender] = useState<'male' | 'female' | ''>('');

  const [fib4Data, setFib4Data] = useState({
    age: '',
    ast: '',
    alt: '',
    platelet: '',
  });

  const [fib3Data, setFib3Data] = useState({
    ast: '',
    alt: '',
    platelet: '',
  });

  const [mfsData, setMfsData] = useState({
    age: '',
    glucose: '',
    bmi: '',
    platelet: '',
    albumin: '',
    ast: '',
    alt: '',
  });

  const [fib4Result, setFib4Result] = useState<{
    score: number;
    riskLevel: string;
    riskColor: string;
    recommendation: string;
  } | null>(null);

  const [fib3Result, setFib3Result] = useState<{
    score: number;
    riskLevel: string;
    riskColor: string;
    recommendation: string;
  } | null>(null);

  const [mfsResult, setMfsResult] = useState<{
    score: number;
    riskLevel: string;
    riskColor: string;
    recommendation: string;
  } | null>(null);

  const handleFib4Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFib4Data(prev => ({ ...prev, [name]: value }));
  };

  const handleFib3Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFib3Data(prev => ({ ...prev, [name]: value }));
  };

  const handleMfsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setMfsData(prev => ({ ...prev, [name]: value }));
  };

  const calculateFIB4 = () => {
    const age = parseFloat(fib4Data.age);
    const ast = parseFloat(fib4Data.ast);
    const alt = parseFloat(fib4Data.alt);
    const platelet = parseFloat(fib4Data.platelet);

    if (!age || !ast || !alt || !platelet) {
      alert('すべての項目を入力してください');
      return;
    }

    if (age < 18 || age > 120) {
      alert('年齢は18〜120の範囲で入力してください');
      return;
    }

    // ✅ 血小板数の単位変換: 万/μL → 10⁹/L
    // 入力値（万/μL）を10倍して 10⁹/L に変換
    const plateletConverted = platelet * 10;

    // FIB-4 Index = (Age × AST) / (PLT × √ALT)
    const score = (age * ast) / (plateletConverted * Math.sqrt(alt));

    let riskLevel = '';
    let riskColor = '';
    let recommendation = '';

    if (score < 1.3) {
      riskLevel = '低リスク';
      riskColor = 'text-green-600';
      recommendation = '肝線維化の進行リスクは低いと考えられます。定期的な健診を継続してください。';
    } else if (score >= 1.3 && score < 2.67) {
      riskLevel = '中リスク';
      riskColor = 'text-orange-600';
      recommendation = '肝線維化の可能性があります。専門医への相談をお勧めします。';
    } else {
      riskLevel = '高リスク';
      riskColor = 'text-red-600';
      recommendation = '肝線維化が進行している可能性が高いです。早急に専門医の診察を受けてください。';
    }

    setFib4Result({
      score: parseFloat(score.toFixed(3)),
      riskLevel,
      riskColor,
      recommendation,
    });
  };

  const calculateFIB3 = () => {
    const ast = parseFloat(fib3Data.ast);
    const alt = parseFloat(fib3Data.alt);
    const platelet = parseFloat(fib3Data.platelet);

    if (!ast || !alt || !platelet) {
      alert('すべての項目を入力してください');
      return;
    }

    // ✅ 血小板数の単位変換: 万/μL → 10⁹/L
    const plateletConverted = platelet * 10;

    // FIB-3 Index = 5 × ln AST - 2 × ln ALT - 0.18 × PLT - 5
    const score = (5 * Math.log(ast)) - (2 * Math.log(alt)) - (0.18 * plateletConverted) - 5;

    let riskLevel = '';
    let riskColor = '';
    let recommendation = '';

    if (score < -1.5) {
      riskLevel = '低リスク';
      riskColor = 'text-green-600';
      recommendation = '肝線維化の進行リスクは低いと考えられます。特に60歳以上の方に有用な指標です。';
    } else if (score >= -1.5 && score < 0.5) {
      riskLevel = '中リスク';
      riskColor = 'text-orange-600';
      recommendation = '肝線維化の可能性があります。精密検査をお勧めします。';
    } else {
      riskLevel = '高リスク';
      riskColor = 'text-red-600';
      recommendation = '肝線維化が進行している可能性が高いです。早急に専門医の診察を受けてください。';
    }

    setFib3Result({
      score: parseFloat(score.toFixed(2)),
      riskLevel,
      riskColor,
      recommendation,
    });
  };

  const calculateMFS = () => {
    const age = parseFloat(mfsData.age);
    const glucose = parseFloat(mfsData.glucose);
    const bmi = parseFloat(mfsData.bmi);
    const platelet = parseFloat(mfsData.platelet);
    const albumin = parseFloat(mfsData.albumin);
    const ast = parseFloat(mfsData.ast);
    const alt = parseFloat(mfsData.alt);

    if (!age || !glucose || !bmi || !platelet || !albumin || !ast || !alt) {
      alert('すべての項目を入力してください');
      return;
    }

    const aar = ast / alt;
    const hyperglycemia = glucose >= 100 ? 1 : 0;

    // ✅ 血小板数の単位変換: 万/μL → 10⁹/L
    const plateletConverted = platelet * 10;

    // MFS = -1.675 + 0.037 × 年齢 + 0.094 × BMI + 1.13 × 耐糖能異常/糖尿病(あり=1, なし=0) + 0.99 × AAR - 0.013 × 血小板 - 0.66 × アルブミン
    const score = -1.675 + 0.037 * age + 0.094 * bmi + 1.13 * hyperglycemia + 0.99 * aar - 0.013 * plateletConverted - 0.66 * albumin;

    let riskLevel = '';
    let riskColor = '';
    let recommendation = '';

    if (score < -1.455) {
      riskLevel = '低リスク';
      riskColor = 'text-green-600';
      recommendation = 'MASLD(代謝機能障害関連脂肪性肝疾患)における肝線維化の進行リスクは低いと考えられます。';
    } else if (score >= -1.455 && score < 0.676) {
      riskLevel = '中リスク';
      riskColor = 'text-orange-600';
      recommendation = '肝線維化の可能性があります。専門医への相談をお勧めします。';
    } else {
      riskLevel = '高リスク';
      riskColor = 'text-red-600';
      recommendation = '肝線維化が進行している可能性が高いです。早急に専門医の診察を受けてください。';
    }

    setMfsResult({
      score: parseFloat(score.toFixed(3)),
      riskLevel,
      riskColor,
      recommendation,
    });
  };

  const resetForms = () => {
    setFib4Data({ age: '', ast: '', alt: '', platelet: '' });
    setFib3Data({ ast: '', alt: '', platelet: '' });
    setMfsData({ age: '', glucose: '', bmi: '', platelet: '', albumin: '', ast: '', alt: '' });
    setFib4Result(null);
    setFib3Result(null);
    setMfsResult(null);
    setGender('');
  };

  const getCurrentResult = () => {
    if (activeCalculator === 'fib4') return fib4Result;
    if (activeCalculator === 'fib3') return fib3Result;
    return mfsResult;
  };

  const currentResult = getCurrentResult();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-pink-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Back Button */}
          <motion.button
            onClick={() => navigate('/')}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8 flex items-center gap-2 text-gray-600 hover:text-rose-600 transition-colors cursor-pointer group"
          >
            <i className="ri-arrow-left-line text-xl group-hover:-translate-x-1 transition-transform"></i>
            <span className="font-medium">ホームに戻る</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              肝臓検査ツール
            </h1>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
              血液検査データから肝線維化リスクを評価します
            </p>
          </motion.div>

          {/* Illustration Section */}
          <div className="flex justify-center items-center gap-8 mb-16 flex-wrap">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-center"
            >
              <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mb-3 shadow-md overflow-hidden">
                <img 
                  src="https://public.readdy.ai/ai/img_res/18d087506c578a9c4916586365243d47.jpg" 
                  alt="肝臓とは" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <p className="text-sm font-bold text-gray-800">肝臓とは？</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center"
            >
              <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mb-3 shadow-md overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=simple%20minimalist%20illustration%20of%20medical%20stethoscope%20and%20health%20checkup%20clipboard%20in%20soft%20coral%20pink%20color%20on%20white%20background%20medical%20icon%20style%20clean%20design%20for%20healthcare%20education&width=200&height=200&seq=liver-icon-02&orientation=squarish" 
                  alt="どんな病気があるの" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <p className="text-sm font-bold text-gray-800">どんな病気があるの？</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center"
            >
              <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mb-3 shadow-md overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=simple%20minimalist%20illustration%20of%20blood%20test%20tube%20and%20medical%20laboratory%20equipment%20in%20soft%20coral%20pink%20color%20on%20white%20background%20medical%20icon%20style%20clean%20design%20for%20healthcare%20education&width=200&height=200&seq=liver-icon-03&orientation=squarish" 
                  alt="検査方法が知りたい" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <p className="text-sm font-bold text-gray-800">検査方法が知りたい！</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="text-center"
            >
              <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center mb-3 shadow-md overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=simple%20minimalist%20illustration%20of%20calculator%20and%20medical%20chart%20with%20data%20analysis%20in%20soft%20coral%20pink%20color%20on%20white%20background%20medical%20icon%20style%20clean%20design%20for%20healthcare%20education&width=200&height=200&seq=liver-icon-04&orientation=squarish" 
                  alt="肝たん計算ツール" 
                  className="w-20 h-20 object-contain"
                />
              </div>
              <p className="text-sm font-bold text-gray-800">肝たん<br />計算ツール</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Calculator Type Selector */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white rounded-full p-1.5 shadow-lg">
              <button
                onClick={() => setActiveCalculator('fib4')}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCalculator === 'fib4'
                    ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                FIB-4 Index
              </button>
              <button
                onClick={() => setActiveCalculator('fib3')}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCalculator === 'fib3'
                    ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                FIB-3 Index
              </button>
              <button
                onClick={() => setActiveCalculator('mfs')}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeCalculator === 'mfs'
                    ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                MFS
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* FIB-4 Calculator */}
            {activeCalculator === 'fib4' && (
              <motion.div
                key="fib4"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-full">
                    <i className="ri-calculator-line text-rose-600 text-xl"></i>
                  </div>
                  FIB-4 Index 計算
                </h2>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  C型肝炎の肝線維化を予測するスコアです。AST、ALT、血小板数、年齢の4項目を組み合わせて計算します。最近ではMASHなどの他の肝疾患にも転用されています。
                </p>

                {/* 計算式の表示 */}
                <div className="mb-6 p-4 bg-gradient-to-r from-rose-50 to-orange-50 rounded-lg border border-rose-200">
                  <p className="text-xs font-bold text-gray-700 mb-2">計算式</p>
                  <div className="text-sm text-gray-800 font-mono">
                    FIB-4 Index = (年齢(歳) × AST(IU/L)) / (血小板数(万/μL) × √ALT(IU/L))
                  </div>
                </div>
                
                <div className="space-y-5">
                  {/* 性別選択 */}
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      性別 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setGender('male')}
                        className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                          gender === 'male'
                            ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        男性
                      </button>
                      <button
                        type="button"
                        onClick={() => setGender('female')}
                        className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                          gender === 'female'
                            ? 'bg-gradient-to-r from-rose-500 to-orange-500 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        女性
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      年齢（歳）
                    </label>
                    <input
                      type="number"
                      name="age"
                      value={fib4Data.age}
                      onChange={handleFib4Change}
                      placeholder="例: 45"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      AST（GOT）（U/L）
                    </label>
                    <input
                      type="number"
                      name="ast"
                      value={fib4Data.ast}
                      onChange={handleFib4Change}
                      placeholder="例: 35"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      ALT（GPT）（U/L）
                    </label>
                    <input
                      type="number"
                      name="alt"
                      value={fib4Data.alt}
                      onChange={handleFib4Change}
                      placeholder="例: 40"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      血小板数（万/μL）
                    </label>
                    <input
                      type="number"
                      name="platelet"
                      value={fib4Data.platelet}
                      onChange={handleFib4Change}
                      placeholder="例: 20"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={calculateFIB4}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white font-bold rounded-lg hover:from-rose-600 hover:to-orange-600 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
                    >
                      計算する
                    </button>
                    <button
                      onClick={resetForms}
                      className="px-6 py-4 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      リセット
                    </button>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-information-line text-orange-600 text-lg"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        <strong>判定基準</strong><br />
                        低値：1.3以下 / 中間値：1.3〜2.67 / 高値：2.67以上
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* FIB-3 Calculator */}
            {activeCalculator === 'fib3' && (
              <motion.div
                key="fib3"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-full">
                    <i className="ri-calculator-line text-rose-600 text-xl"></i>
                  </div>
                  FIB-3 Index 計算
                </h2>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  FIB-4 Indexから年齢を除いた3つの因子（AST・ALT・血小板値）のみで構成されたスコアです。特に60歳以上の高齢者において、FIB-4 Indexを上回る肝線維化予測能を有する可能性があります。
                </p>

                {/* 計算式の表示 */}
                <div className="mb-6 p-4 bg-gradient-to-r from-rose-50 to-orange-50 rounded-lg border border-rose-200">
                  <p className="text-xs font-bold text-gray-700 mb-2">計算式</p>
                  <div className="text-sm text-gray-800 font-mono leading-relaxed">
                    FIB-3 Index = 5 × ln AST(IU/L) - 2 × ln ALT(IU/L) - 0.18 × PLT(万/μL) - 5
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    ※ ln = 自然対数、PLT = 血小板数
                  </p>
                </div>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      AST（GOT）（U/L）
                    </label>
                    <input
                      type="number"
                      name="ast"
                      value={fib3Data.ast}
                      onChange={handleFib3Change}
                      placeholder="例: 35"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      ALT（GPT）（U/L）
                    </label>
                    <input
                      type="number"
                      name="alt"
                      value={fib3Data.alt}
                      onChange={handleFib3Change}
                      placeholder="例: 40"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      血小板数（万/μL）
                    </label>
                    <input
                      type="number"
                      name="platelet"
                      value={fib3Data.platelet}
                      onChange={handleFib3Change}
                      placeholder="例: 20"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={calculateFIB3}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white font-bold rounded-lg hover:from-rose-600 hover:to-orange-600 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
                    >
                      計算する
                    </button>
                    <button
                      onClick={resetForms}
                      className="px-6 py-4 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      リセット
                    </button>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-information-line text-orange-600 text-lg"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        <strong>特徴</strong><br />
                        年齢因子を除外することで、高齢者における診断精度が向上します。
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* MFS Calculator */}
            {activeCalculator === 'mfs' && (
              <motion.div
                key="mfs"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-full">
                    <i className="ri-calculator-line text-rose-600 text-xl"></i>
                  </div>
                  MFS 計算
                </h2>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  MASLD（代謝機能障害関連脂肪性肝疾患）における肝線維化を予測するスコアです。年齢、高血糖、BMI、血小板数、アルブミン値、AARを組み合わせて計算します。
                </p>

                {/* 計算式の表示 */}
                <div className="mb-6 p-4 bg-gradient-to-r from-rose-50 to-orange-50 rounded-lg border border-rose-200">
                  <p className="text-xs font-bold text-gray-700 mb-2">計算式</p>
                  <div className="text-xs text-gray-800 leading-relaxed space-y-1">
                    <div className="font-semibold">MASLD Fibrosis Score =</div>
                    <div className="ml-2">
                      -1.675 + 0.037 x 年齢(歳) + 0.094 x BMI(kg/m²)
                    </div>
                    <div className="ml-2">
                      + 1.13 x 耐糖能異常/糖尿病(あり=1, なし=0)
                    </div>
                    <div className="ml-2">
                      + 0.99 x AST/ALT - 0.013 x 血小板(万/μL)
                    </div>
                    <div className="ml-2">
                      - 0.66 x アルブミン(g/dL)
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    ※ 耐糖能異常/糖尿病: 空腹時血糖値 ≥100 mg/dL の場合は1
                  </p>
                </div>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      年齢（歳）
                    </label>
                    <input
                      type="number"
                      name="age"
                      value={mfsData.age}
                      onChange={handleMfsChange}
                      placeholder="例: 45"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      空腹時血糖値（mg/dL）
                    </label>
                    <input
                      type="number"
                      name="glucose"
                      value={mfsData.glucose}
                      onChange={handleMfsChange}
                      placeholder="例: 95"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      BMI（体格指数）
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      name="bmi"
                      value={mfsData.bmi}
                      onChange={handleMfsChange}
                      placeholder="例: 24.5"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      血小板数（万/μL）
                    </label>
                    <input
                      type="number"
                      name="platelet"
                      value={mfsData.platelet}
                      onChange={handleMfsChange}
                      placeholder="例: 20"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      アルブミン（g/dL）
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      name="albumin"
                      value={mfsData.albumin}
                      onChange={handleMfsChange}
                      placeholder="例: 4.2"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      AST（GOT）（U/L）
                    </label>
                    <input
                      type="number"
                      name="ast"
                      value={mfsData.ast}
                      onChange={handleMfsChange}
                      placeholder="例: 35"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-800 mb-2">
                      ALT（GPT）（U/L）
                    </label>
                    <input
                      type="number"
                      name="alt"
                      value={mfsData.alt}
                      onChange={handleMfsChange}
                      placeholder="例: 40"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-rose-400 focus:outline-none transition-colors text-base"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={calculateMFS}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white font-bold rounded-lg hover:from-rose-600 hover:to-orange-600 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
                    >
                      計算する
                    </button>
                    <button
                      onClick={resetForms}
                      className="px-6 py-4 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      リセット
                    </button>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-information-line text-orange-600 text-lg"></i>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        <strong>MFSとは</strong><br />
                        もともとNFS（NAFLD Fibrosis Score）と呼ばれていたスコアです。代謝因子を含む包括的な評価が特徴です。
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Results Section */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="lg:sticky lg:top-32"
            >
              {currentResult ? (
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-full">
                      <i className="ri-file-chart-line text-rose-600 text-xl"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">計算結果</h2>
                  </div>

                  <div className="mb-6 p-6 bg-gradient-to-br from-orange-50 to-rose-50 rounded-xl border-2 border-rose-200">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">
                        {activeCalculator === 'fib4' && 'FIB-4 Index'}
                        {activeCalculator === 'fib3' && 'FIB-3 Index'}
                        {activeCalculator === 'mfs' && 'MFS スコア'}
                      </p>
                      <p className="text-5xl font-bold text-gray-900 mb-3">{currentResult.score}</p>
                      <div className={`inline-block px-4 py-2 rounded-full font-bold text-lg ${
                        currentResult.riskLevel === '低リスク' ? 'bg-green-100 text-green-700' :
                        currentResult.riskLevel === '中リスク' ? 'bg-orange-100 text-orange-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {currentResult.riskLevel}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {activeCalculator === 'fib4' && (
                      <div>
                        <h3 className="text-base font-bold text-gray-900 mb-3">判定基準</h3>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="w-3 h-3 bg-green-500 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-800"><strong>&lt; 1.3</strong>：低リスク</span>
                          </div>
                          <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                            <div className="w-3 h-3 bg-orange-500 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-800"><strong>1.3 - 2.67</strong>：中リスク</span>
                          </div>
                          <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                            <div className="w-3 h-3 bg-red-500 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-800"><strong>&gt; 2.67</strong>：高リスク</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeCalculator === 'fib3' && (
                      <div>
                        <h3 className="text-base font-bold text-gray-900 mb-3">判定基準</h3>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="w-3 h-3 bg-green-500 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-800"><strong>&lt; -1.5</strong>：低リスク</span>
                          </div>
                          <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                            <div className="w-3 h-3 bg-orange-500 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-800"><strong>-1.5 - 0.5</strong>：中リスク</span>
                          </div>
                          <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                            <div className="w-3 h-3 bg-red-500 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-800"><strong>&gt; 0.5</strong>：高リスク</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeCalculator === 'mfs' && (
                      <div>
                        <h3 className="text-base font-bold text-gray-900 mb-3">判定基準</h3>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="w-3 h-3 bg-green-500 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-800"><strong>&lt; -1.455</strong>：低リスク</span>
                          </div>
                          <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                            <div className="w-3 h-3 bg-orange-500 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-800"><strong>-1.455 - 0.676</strong>：中リスク</span>
                          </div>
                          <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                            <div className="w-3 h-3 bg-red-500 rounded-full flex-shrink-0"></div>
                            <span className="text-sm text-gray-800"><strong>&gt; 0.676</strong>：高リスク</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="p-5 bg-blue-50 rounded-xl border border-blue-200">
                      <h3 className="text-base font-bold text-gray-900 mb-2 flex items-center gap-2">
                        <i className="ri-stethoscope-line text-blue-600"></i>
                        推奨アクション
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed">{currentResult.recommendation}</p>
                    </div>

                    <div className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
                      <p className="text-xs text-gray-700 leading-relaxed">
                        <strong>※ 注意事項</strong><br />
                        この計算結果は参考値です。診断には医師の判断が必要です。気になる症状がある場合は、必ず医療機関を受診してください。
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-rose-100 to-orange-100 rounded-full flex items-center justify-center">
                      <i className="ri-flask-line text-rose-600 text-4xl"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">計算の準備ができました</h3>
                    <p className="text-gray-600 leading-relaxed text-sm mb-6">
                      左側のフォームに血液検査データを入力して、スコアを計算してください。
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-check-line text-green-600 text-base"></i>
                        </div>
                        <p className="text-sm text-gray-700">即座に結果を表示</p>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-check-line text-green-600 text-base"></i>
                        </div>
                        <p className="text-sm text-gray-700">医学的根拠に基づく評価</p>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-check-line text-green-600 text-base"></i>
                        </div>
                        <p className="text-sm text-gray-700">個別の推奨アクション提示</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section ref={ref} className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">3つの検査方法について</h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              それぞれの特徴を理解して、適切な検査方法を選択しましょう
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-xl shadow-md border border-blue-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-blue-100 rounded-full mb-4">
                <i className="ri-test-tube-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">FIB-4 Index</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                最も広く使用されている肝線維化予測スコア。C型肝炎やMASHなど幅広い肝疾患に対応。
              </p>
              <p className="text-xs text-gray-500">
                使用項目：年齢、AST、ALT、血小板数
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-gradient-to-br from-green-50 to-white p-6 rounded-xl shadow-md border border-green-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-user-heart-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">FIB-3 Index</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                年齢因子を除外したスコア。特に60歳以上の高齢者において高い予測精度を発揮。
              </p>
              <p className="text-xs text-gray-500">
                使用項目：AST、ALT、血小板数
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-gradient-to-br from-purple-50 to-white p-6 rounded-xl shadow-md border border-purple-100"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-purple-100 rounded-full mb-4">
                <i className="ri-heart-pulse-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">MFS</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-3">
                MASLD（代謝機能障害関連脂肪性肝疾患）に特化したスコア。代謝因子も考慮。
              </p>
              <p className="text-xs text-gray-500">
                使用項目：年齢、血糖値、BMI、血小板数、アルブミン、AST、ALT
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-rose-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              企業健診でのご利用をご検討の方へ
            </h2>
            <p className="text-white/90 text-base mb-8 leading-relaxed">
              Liver Insight Platformは、企業健診データと連携し、<br />
              従業員の肝臓健康管理をサポートします。
            </p>
            <button className="px-8 py-4 bg-white text-rose-600 font-bold rounded-lg hover:bg-gray-50 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer">
              企業向けプランを見る
            </button>
          </motion.div>
        </div>
      </section>

      {/* 改訂履歴・注意事項セクション */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl p-8 shadow-md border border-gray-200"
          >
            {/* 改訂履歴 */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-full">
                  <i className="ri-history-line text-blue-600 text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">改訂履歴</h3>
              </div>
              <div className="pl-11">
                <div className="flex items-start gap-3 mb-2">
                  <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">2026年1月29日：</span>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    FIB-4指数の計算において、血小板数の単位を10⁹/L(10³/μLと同等)から、日本国内で一般的に用いられる万/μLに変更しました。
                  </p>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="p-5 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-alert-line text-yellow-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">注意事項</h4>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    入力の際は、お手元の検査結果にある「万」の数値をそのままご入力ください。本サイトでの計算結果は、入力された単位(万/μL)に基づき最適化されています。過去のデータや他サイトの数値と比較される際は、単位に相違がないかご確認ください。
                  </p>
                </div>
              </div>
            </div>

            {/* 補足情報 */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 leading-relaxed">
                ※ 血小板数の基準値は一般的に15〜45万/μLです。入力時は単位にご注意ください。<br />
                ※ 本ツールは医療診断を目的としたものではありません。結果はあくまで参考値としてご利用ください。
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FIB4CalculatorPage;
